/**
 * ProviderDetail — V101-1-10. One provider in four compact sections:
 * Components · Supplier · Boundary · Connector. A modal DS Drawer opened from
 * ProviderSuggestions rows (Setup, SystemDetail).
 *
 * API: GET /api/w/:slug/providers/:providerKey (any member; 404 unknown).
 * Every empty section carries a way forward (no dead ends).
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Drawer, Spinner, StatusGlyph } from '@aigovops/design-system';
import { getProviderView, type PostureConnector, type ProviderBoundaryGap, type ProviderView } from '../lib/api';
import { queryKeys } from '../lib/query-keys';
import { providerName } from '../lib/providers';
import { AskAdmin } from './AskAdmin';
import { connectorHealth, privilegeBadgeLabel, relativeTime } from './PostureConnections';
import './provider-suggestions.css';

const CHANNEL_LABEL: Record<string, string> = {
  direct: 'Direct API', bedrock: 'AWS Bedrock', vertex: 'Vertex AI', azure_openai: 'Azure OpenAI',
  gateway: 'AI gateway', self_hosted: 'Self-hosted', ollama_cloud: 'Ollama Cloud',
};
export const channelLabel = (c: string) => CHANNEL_LABEL[c] ?? providerName(c);

const FEDRAMP_LABEL: Record<string, string> = {
  authorized: 'FedRAMP authorized', in_process: 'FedRAMP in process', ready: 'FedRAMP ready',
  not_authorized: 'Not FedRAMP authorized', unknown: 'FedRAMP status unknown',
};

export const GAP_LABEL: Record<ProviderBoundaryGap, string> = {
  no_report: 'No SOC 2 report',
  federal_data_not_authorized: 'Federal data, not FedRAMP authorized',
};

const TIER_LABEL: Record<string, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

/** The view's connector row in the shape connectorHealth reads. */
function asPosture(c: ProviderView['connectors'][number]): PostureConnector {
  return {
    connector_id: c.connector_id, kind: c.kind as PostureConnector['kind'], status: (c.status === 'active' || c.status === 'disabled' || c.status === 'error') ? c.status : 'active',
    display_name: c.label, target: null, scopes: [], configured_at: '', last_run_at: c.last_run_at, last_error_at: null, last_error_message: c.last_error_message,
    checks: { pass: 0, fail: 0, last_observed_at: null },
  };
}

/**
 * One row per system using the provider: the union of SBOM components and
 * systems that list it, the opened-from system first.
 */
export function systemRows(v: Pick<ProviderView, 'components' | 'systems'>, systemId?: string): Array<{ system_id: string; name: string; components: number; access_channels: string[] }> {
  const by = new Map<string, { system_id: string; name: string; components: number; access_channels: string[] }>();
  for (const c of v.components) by.set(c.system_id, { system_id: c.system_id, name: c.system_name, components: c.components, access_channels: c.access_channels });
  for (const sys of v.systems) if (!by.has(sys.system_id)) by.set(sys.system_id, { system_id: sys.system_id, name: sys.name, components: 0, access_channels: [] });
  return [...by.values()].sort((a, b) => (a.system_id === systemId ? -1 : b.system_id === systemId ? 1 : a.name.localeCompare(b.name)));
}

export function ProviderDetail({ slug, providerKey, editable, onClose, onConnect, systemId }: {
  slug: string; providerKey: string; editable: boolean; onClose: () => void; onConnect: (kind: string) => void;
  /** Opened from this system: listed first and marked "This system". */
  systemId?: string;
}) {
  const q = useQuery({ queryKey: queryKeys.providerView(slug, providerKey), queryFn: () => getProviderView(slug, providerKey), staleTime: 60_000, retry: false });
  const v = q.data;
  const name = v?.provider.name ?? providerName(providerKey);
  const prefix = `/w/${slug}/app`;
  return <Drawer open modal onClose={onClose} label={name} className="v96-drawer v101-provider">
    <div className="v96-drawer-head">
      <h2>{name}</h2>
      <Button variant="ghost" onClick={onClose} aria-label="Close">✕</Button>
    </div>
    {q.isLoading ? <Spinner label={`Loading ${name}`} />
      : q.error || !v ? <Alert variant="error"><p>{name} could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>
      : <div className="v101-provider-sections">
        <section aria-labelledby="pv-components">
          <h3 id="pv-components">Systems</h3>
          {(() => {
            const rows = systemRows(v, systemId);
            return rows.length > 0 ? <ul className="v101-pv-list">
              {rows.map(r => <li key={r.system_id}>
                <Link to={`${prefix}/systems/${encodeURIComponent(r.system_id)}`} onClick={onClose}>{r.name}</Link>
                {r.system_id === systemId && <Badge className="v75-status neutral">This system</Badge>}
                <span className="v75-muted">{[r.components > 0 ? `${r.components} ${r.components === 1 ? 'component' : 'components'}` : 'Listed', ...r.access_channels.map(channelLabel)].map(t => `· ${t}`).join(' ')}</span>
              </li>)}
            </ul>
              : <p className="v101-pv-empty"><span className="v75-muted">Not in any system</span> <Link to={`${prefix}/systems`} onClick={onClose}>Open systems</Link></p>;
          })()}
        </section>

        <section aria-labelledby="pv-supplier">
          <h3 id="pv-supplier">Supplier</h3>
          {v.supplier
            ? <p className="v101-pv-row">{!v.supplier.risk_tier && <span className="v75-muted">In your suppliers</span>}{v.supplier.risk_tier && <Badge className={`v75-status ${v.supplier.risk_tier === 'critical' || v.supplier.risk_tier === 'high' ? 'warning' : 'neutral'}`}>{`${TIER_LABEL[v.supplier.risk_tier] ?? v.supplier.risk_tier} risk`}</Badge>}<Link to={`${prefix}/suppliers`} onClick={onClose}>Open suppliers</Link></p>
            : <p className="v101-pv-empty"><span className="v75-muted">Not in your suppliers</span> <Link to={`${prefix}/suppliers`} onClick={onClose}>Open suppliers</Link></p>}
        </section>

        <section aria-labelledby="pv-boundary">
          <h3 id="pv-boundary">Boundary</h3>
          {v.boundary
            ? <>
              <p className="v101-pv-row">
                {/* The federal-data gap already says "not FedRAMP authorized"; don't repeat it as a neutral badge. */}
                {!v.boundary.gaps.includes('federal_data_not_authorized') && <Badge>{FEDRAMP_LABEL[v.boundary.fedramp_status] ?? v.boundary.fedramp_status}</Badge>}
                <Link to={`${prefix}/boundary`} onClick={onClose}>Open boundary</Link></p>
              {v.boundary.gaps.length > 0 && <ul className="v101-chips" aria-label="Boundary gaps">
                {v.boundary.gaps.map(g => <li key={g}><Link className="v101-gap" to={`${prefix}/boundary`} onClick={onClose}><StatusGlyph kind="warning" /> {GAP_LABEL[g]} <span aria-hidden="true">→</span></Link></li>)}
              </ul>}
            </>
            : <p className="v101-pv-empty"><span className="v75-muted">Not in your boundary</span> <Link to={`${prefix}/boundary`} onClick={onClose}>Open boundary</Link></p>}
        </section>

        <section aria-labelledby="pv-connector">
          <h3 id="pv-connector">Connector</h3>
          {v.connectors.length > 0 ? <ul className="v101-pv-list">
            {v.connectors.map(c => {
              const h = connectorHealth(asPosture(c));
              const card = `${prefix}/setup#posture-${encodeURIComponent(c.connector_id)}`;
              return <li key={c.connector_id}>
                <Link to={card} onClick={onClose}>{c.label}</Link>
                <Badge className={`v75-status ${h.tone}`}><StatusGlyph kind={h.glyph} /> {h.label}</Badge>
                {c.privilege && <Badge className="v75-status warning"><StatusGlyph kind="warning" /> {privilegeBadgeLabel(c.kind)}</Badge>}
                <span className="v75-muted">{relativeTime(c.last_run_at)}</span>
                {(h.key === 'error' || h.key === 'stale') && (editable
                  ? <Link className="v75-action" to={card} onClick={onClose}>{h.key === 'error' ? 'Fix connection' : 'Collect now'}</Link>
                  : <AskAdmin slug={slug} compact verb={h.key === 'error' ? `fix ${c.label}` : `collect ${c.label}`} what={`manages ${c.label}`} subject={`${c.label} connection in GAC Pro`} />)}
              </li>;
            })}
          </ul>
            : v.suggestion ? (editable
              ? <div className="v75-actions"><Button variant="primary" onClick={() => onConnect(v.suggestion!.connector_kind)}>{`Connect ${v.suggestion.label}`}</Button>{v.suggestion.privilege && <Badge className="v75-status warning"><StatusGlyph kind="warning" /> Uses an admin key</Badge>}</div>
              : <AskAdmin slug={slug} what={`connects ${v.suggestion.label}`} subject={`Connect ${v.suggestion.label} in GAC Pro`} />)
            : <p className="v101-pv-empty"><span className="v75-muted">No connector available</span> <Link to={`${prefix}/setup#posture-families`} onClick={onClose}>Integrations directory</Link></p>}
        </section>
      </div>}
  </Drawer>;
}
